import { Injectable } from "@angular/core";
import { Observable, lastValueFrom, map, retry, switchMap, take } from "rxjs";
import { ApiService } from "../api/api.service";
import { ApiGetInterface } from "@core/models/apiServiceInterface.interface";
import { InstanceView } from "@core/models/instance-view.interface";
import { GoogleService } from "@shared/services/google.service";
import { GooglePlaceResult, GoogleResult } from "@shared/models/google-result.interface";
import { PageResponse } from "@shared/models/pageResponse.interface";

@Injectable({
  providedIn: "root",
})
export class ActiveInstanceService {
  constructor(
    private apiService: ApiService,
    private googleService: GoogleService
  ) {}

  getInstance(): InstanceView | null {
    const selectedInstance = localStorage.getItem("instance");
    return selectedInstance ? JSON.parse(selectedInstance) : null;
  }

  setInstanceLocally(instance: InstanceView): void {
    if (instance) {
      localStorage.setItem('instance', JSON.stringify(instance));
    }
  }

  getInstanceCurrency(): string {
    const selectedInstance = this.getInstance();
    if (
      selectedInstance &&
      selectedInstance.currency &&
      selectedInstance.currency.code
    ) {
      return selectedInstance.currency.code;
    }
    return "";
  }

  private getSmbInstance(): Observable<InstanceView> {
    const payload: ApiGetInterface = {
      url: `dsp/instances`,
    };

    return this.apiService.get<PageResponse<InstanceView>>(payload).pipe(
        map(res => res.content[0])
    );
  }

  async setInstance(): Promise<void> {
    let smbInstance: InstanceView;
    const googleResult$ = this.getSmbInstance()
      .pipe(
        take(1),
        map((instance: InstanceView) => {
          smbInstance = instance;
          return instance.country.name;
        }),
        switchMap((instanceCountryName: string) => {
          return this.googleService
            .getPlacesFromGoogle(instanceCountryName, null)
            .pipe(
              retry(1),
              map((googleRes) => {
                const instancLocation = googleRes?.predictions.find((result) =>
                  result.description.toLowerCase() === instanceCountryName.toLowerCase()
                );

                if (!instancLocation) {
                  throw new Error('Google location prediction not found');
                }

                return instancLocation; // now TypeScript knows it's not undefined
              })
            );
        }),
        switchMap((instanceLocation: GoogleResult) => {
         return this.googleService.getGooglePlaceInfo(instanceLocation.place_id).pipe(
            retry(1),
            map((googlePlaceRes: GooglePlaceResult) => {
              const location = {
                ...instanceLocation,
                ...googlePlaceRes
              }
              return location;
            })
          );
        })
      );
      let location = await lastValueFrom(googleResult$);
      const { countryCode, country } = this.googleService.getCountryCode(location);
      location = {
        ...location,
        countryCode,
        country,
      }

      await this.googleService.drawBboxArea(
        location?.geometry?.viewport
      ).toPromise().then(value => {
        location.area = value;
      }).finally(() => {
        const instance = {
          ...smbInstance,
          location,
        }
        localStorage.setItem("instance", JSON.stringify(instance));
      });
  }
}
